import { type Collection, List as ImmutableList, Map as ImmutableMap, OrderedMap } from 'immutable';
import _isEqual from 'lodash/isEqual';
import { createSelectorCreator, lruMemoize, weakMapMemoize } from 'reselect';
import { v4 as uuidv4 } from 'uuid';

import { registerCacheReset } from 'app-components/utils/core/reselect/selectorCacheRegistry';

import { getResourceChangesSince, getResourceVersion } from './resourceChangeLog';

// compares 2 objects deep (obj1.obj and obj2.obj) or shallow (obj1.obj and obj2.obj) based on 'obj.deep' flag
export const flexEqual = <T extends { deep?: boolean; obj: object }>(obj1?: T | null, obj2?: T | null) => {
  let isEqual;

  if (
    obj1 &&
    obj2 &&
    Object.prototype.hasOwnProperty.call(obj1, 'obj') &&
    Object.prototype.hasOwnProperty.call(obj2, 'obj')
  ) {
    if (obj1.deep && obj2.deep) {
      isEqual = JSON.stringify(obj1.obj) === JSON.stringify(obj2.obj); // deep equal
    } else {
      isEqual = obj1.obj === obj2.obj; // shallow equal
    }
  } else {
    isEqual = obj1 === obj2; // standard equal
  }

  return isEqual;
};

// arrays shallow compare
export const arraysEqualShallow = <T>(array1?: T[], array2?: T[]) =>
  Boolean(
    (!array1 && !array2) ||
      (array1 &&
        array2 &&
        array1.length === array2.length &&
        array1.every((element, index) => element === array2[index])),
  );

export const createArraysEqualShallowSelector = createSelectorCreator(lruMemoize, arraysEqualShallow);

export function createTrimSelector<S, Params extends unknown[], V, R>(
  selector: (root: S, ...params: Params) => ImmutableList<V>,
  trimmer: (val: V) => R,
): (root: S, ...params: Params) => ImmutableList<R>;
export function createTrimSelector<S, Params extends unknown[], K, V, R>(
  selector: (root: S, ...params: Params) => ImmutableMap<K, V>,
  trimmer: (val: V) => R,
): (root: S, ...params: Params) => ImmutableMap<K, R>;
export function createTrimSelector<S, Params extends unknown[], K, V, R>(
  selector: (root: S, ...params: Params) => Collection<K, V>,
  trimmer: (val: V) => R,
): (root: S, ...params: Params) => Collection<K, R> {
  let dataTrimmedCache: Collection<K, R> | undefined;
  registerCacheReset(() => {
    dataTrimmedCache = undefined;
  });
  // What createSelector does for one input selector, with the selector's own parameter types.
  const trim = weakMapMemoize((dataReal: Collection<K, V>) => {
    const dataTrimmedReal = dataReal.map(trimmer);
    if (dataTrimmedCache && _isEqual(dataTrimmedReal, dataTrimmedCache)) return dataTrimmedCache;
    dataTrimmedCache = dataTrimmedReal;
    return dataTrimmedReal;
  });
  return (root, ...params) => trim(selector(root, ...params));
}

type IncrementalFilterState<R> = { version: number; size: number; inputs: readonly unknown[]; result: R };

// Immutable keeps maps of up to 8 entries in insertion order; larger maps iterate in hash order.
const MAX_INSERTION_ORDERED_SIZE = 8;
const MAX_INCREMENTAL_FILTER_STATES = 16;

const sameInputs = (previous: readonly unknown[], next: readonly unknown[]) =>
  previous.length === next.length && previous.every((input, index) => input === next[index]);

function filterChanges<V, R extends ImmutableMap<string, V>>(
  previous: IncrementalFilterState<R>,
  resourcesById: ImmutableMap<string, V>,
  test: (value: V) => unknown,
): R | undefined {
  // Without deletes, maps of more than 8 entries keep every entry's place: Immutable orders them by
  // hash, and only deletes move entries (within hash-colliding keys and in maps of up to 8 entries).
  if (previous.size <= MAX_INSERTION_ORDERED_SIZE || resourcesById.size <= MAX_INSERTION_ORDERED_SIZE) return undefined;
  const changes = getResourceChangesSince(resourcesById, previous.version);
  if (!changes || changes.removed) return undefined;
  let result = previous.result;
  let leavers: Set<string> | undefined;
  for (const key of changes.keys) {
    const value = resourcesById.get(key);
    if (value === undefined) return undefined;
    if (test(value)) {
      // A joining record takes its place from the map's order.
      if (!previous.result.has(key)) return undefined;
      if (result.get(key) !== value) result = result.set(key, value);
    } else if (previous.result.has(key)) {
      leavers = leavers ?? new Set();
      leavers.add(key);
    }
  }
  const left = leavers;
  if (!left) return result;
  // A plain Map rebuilt from fewer entries can order fully hash-colliding keys differently, so only
  // ordered results drop leavers in place (filter keeps the order; delete can move entries).
  return OrderedMap.isOrderedMap(result) ? result.filter((_value, key) => !left.has(key)) : undefined;
}

/**
 * Like `filterAll`, but after logged upserts with the same `inputs` it re-tests only the changed records.
 * Results match `filterAll` exactly, including order; anything that could reorder falls back to it.
 * re-reselect shares one result function across cache keys, so cached selectors pass their key.
 * `test` returns a truthy value like any Immutable predicate, so `filterAll` can hand it to `filter` as is.
 */
export function createIncrementalFilter<V, R extends ImmutableMap<string, V>>(
  filterAll: (resourcesById: ImmutableMap<string, V>, test: (value: V) => unknown) => R,
): (
  resourcesById: ImmutableMap<string, V>,
  inputs: readonly unknown[],
  test: (value: V) => unknown,
  cacheKey?: unknown,
) => R {
  const states = new Map<unknown, IncrementalFilterState<R>>();
  registerCacheReset(() => states.clear());
  return (
    resourcesById: ImmutableMap<string, V>,
    inputs: readonly unknown[],
    test: (value: V) => unknown,
    cacheKey?: unknown,
  ): R => {
    const previous = states.get(cacheKey);
    const updated =
      previous && sameInputs(previous.inputs, inputs) ? filterChanges(previous, resourcesById, test) : undefined;
    const result = updated ?? filterAll(resourcesById, test);
    if (!updated) {
      // Drop states this map no longer descends from (a full sync or another user), so they don't linger.
      states.forEach((state, key) => {
        if (key !== cacheKey && !getResourceChangesSince(resourcesById, state.version)) states.delete(key);
      });
    }
    states.delete(cacheKey);
    states.set(cacheKey, { version: getResourceVersion(resourcesById), size: resourcesById.size, inputs, result });
    if (states.size > MAX_INCREMENTAL_FILTER_STATES) states.delete(states.keys().next().value);
    return result;
  };
}

export function createValueCache<T extends unknown[], R>(getValue: (...args: T) => R): (...params: T) => R {
  let cached: { value: R } | undefined;
  registerCacheReset(() => {
    cached = undefined;
  });
  return (...params: T) => {
    const value = getValue(...params);
    if (!cached || !_isEqual(cached.value, value)) cached = { value };
    return cached.value;
  };
}

type SimpleObject = { readonly [key: string]: unknown };
const allItemsEqual = (obj1: SimpleObject, obj2: SimpleObject) => {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;

  return !keys1.some((key) => obj2[key] !== obj1[key]);
};

type ObjectString = { cacheObject: SimpleObject; cacheString: string };
let objectStringMap = ImmutableMap<string, ImmutableList<ObjectString>>();

/** Whether the value cached under `cacheString` is still in use; falsy ones are collected. */
type InUseFn = (name: string, cacheString: string) => unknown;
function garbageCollectStringMap(name: string, inUseFn: InUseFn) {
  const newList = objectStringMap.get(name)?.filter((cacheItem) => inUseFn(name, cacheItem.cacheString));

  if (newList) {
    objectStringMap = objectStringMap.set(name, newList);
  }
}

export const getOrCreateStringFromObject = (
  name: string,
  cacheObject: SimpleObject,
  maxSize = 5,
  inUseFn?: InUseFn,
) => {
  const objectStringList = objectStringMap.get(name) || ImmutableList();

  if (inUseFn && objectStringList?.size > maxSize + 1) {
    garbageCollectStringMap(name, inUseFn);
  }

  const cacheItem = objectStringList.find((item) => allItemsEqual(cacheObject, item.cacheObject));
  if (cacheItem) return cacheItem.cacheString;
  const cacheString = uuidv4();
  objectStringMap = objectStringMap.set(name, objectStringList.push({ cacheObject, cacheString }));
  return cacheString;
};

type CacheEntry = { key: string; value: unknown };
let memoCache = ImmutableMap<string, ImmutableList<CacheEntry>>();
registerCacheReset(() => {
  objectStringMap = ImmutableMap();
  memoCache = ImmutableMap();
});

/*
 *  MEMOIZE WITH KEY SET CACHE
 */
export function memoizeWithKeySetCache(cacheName: string, key: string, value: unknown, maxSize = 10) {
  let thisCache = memoCache.get(cacheName);

  // if this key already exists, remove it
  if (thisCache) {
    const index = thisCache.findIndex((x) => x.key === key);
    if (index !== -1) thisCache = thisCache.splice(index, 1);
  }

  if (!thisCache) thisCache = ImmutableList([]);
  thisCache = thisCache.push({ key, value });
  if (thisCache.size > maxSize) {
    thisCache = thisCache.slice(1);
  }
  memoCache = memoCache.set(cacheName, thisCache);
}

/*
 * memoizeWithKey Functions
 *
 * Memoize functions where you can control both the key, and the size of the cache
 */

/** A cache name holds one type of value: every call with the same name must compute the same type. */
export function memoizeWithKey<T>(cacheName: string, valueFn: () => T, key: string, maxSize?: number): T;
export function memoizeWithKey(cacheName: string, valueFn: () => unknown, key: string, maxSize = 10): unknown {
  const thisCache = memoCache.get(cacheName);
  if (thisCache) {
    const cached = thisCache.find((x) => x.key === key);
    if (cached) return cached.value;
  }

  const ret = valueFn();
  memoizeWithKeySetCache(cacheName, key, ret, maxSize);
  return ret;
}

/*
 *  MEMOIZE WITH KEY RETRIEVE CACHE
 */
export function memoizeWithKeyRetrieveCache(cacheName: string, key: string): unknown {
  const thisCache = memoCache.get(cacheName);
  if (!thisCache) return undefined;

  const cached = thisCache.find((x) => x.key === key);
  if (cached) return cached.value;
  return null;
}
